import { LOCALE_STORAGE_KEY } from '@futrli/localisation';
import { DIGITS, LOWERCASE, SPECIAL_CHARACTERS, UPPERCASE } from '../regex';

const LS_PERSISTENT_KEYS = [LOCALE_STORAGE_KEY];

/**
 * Gets the initials of a string passed as parameter.
 * @param {string} value - String to get the initials from.
 * @param {number} numberOfInitials - Number of initials we want to show.
 * @param {boolean} toUpperCase - Sets whether the initials will be uppercase or not.
 * @returns {string} - Initials.
 */
export const getInitials = (value: string, numberOfInitials?: number, toUpperCase = true) => {
  let initialsArray = value.match(/\b(\w)/g);

  if (!initialsArray) {
    return '';
  }

  if (numberOfInitials) {
    initialsArray = initialsArray.slice(0, numberOfInitials) as RegExpMatchArray;
  }

  return toUpperCase ? initialsArray.join('').toUpperCase() : initialsArray.join('');
};

export const hasDigits = (value: string) => DIGITS.test(value);
export const hasUppercaseCharacters = (value: string) => UPPERCASE.test(value);
export const hasLowercaseCharacters = (value: string) => LOWERCASE.test(value);

/**
 * Special characters for cognito user pool password. (Does not include + or = as such)
 * @see https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
 *
 * @param {string} value - string
 * @returns {boolean}
 */
export const hasSpecialCharacters = (value: string) => SPECIAL_CHARACTERS.test(value);

/**
 * Prefixes a given key to be saved on a Storage (Local, Session...) with a common pattern.
 * @param {string} key to be prefixed
 * @returns {string}
 */
export const prefixStorageKey = (key: string) => `futrli.${key}`;

export const clearLocalStorageWithExceptions = (exceptions: string[] = LS_PERSISTENT_KEYS) => {
  const keysToRemove = Object.keys(localStorage).filter(key => !exceptions.includes(key));
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
};

/**
 * Generates a unique id joining differents ids.
 * It cleans empty values before join them.
 * @param {string[]} ids
 * @returns {string} unique id
 */
export const joinUniqId = (...ids: string[]): string => ids.filter(Boolean).join('::');

// Unlike joinUniqId, preserve empty positions and avoid delimiter collisions.
export const getResourceKey = (...parts: (string | number | boolean | null | undefined)[]): string =>
  JSON.stringify(parts);

/**
 * Splits an unique id in those ids which it was formed of.
 * @param {string} id unique id
 * @returns {string[]} ids
 */
export const splitUniqId = (id: string): string[] => id.split('::');
